import { useMeeting, useParticipant } from "@videosdk.live/react-sdk";
import React from "react";
import MicOffIcon from "../../../assets/icons/ParticipantTabPanel/MicOffIcon";
import MicOnIcon from "../../../assets/icons/ParticipantTabPanel/MicOnIcon";
import RaiseHand from "../../../assets/icons/ParticipantTabPanel/RaiseHand";
import VideoCamOffIcon from "../../../assets/icons/ParticipantTabPanel/VideoCamOffIcon";
import VideoCamOnIcon from "../../../assets/icons/ParticipantTabPanel/VideoCamOnIcon";
import { useMeetingAppContext } from "../../../hooks/MeetingAppContextDef";
import { nameTructed } from "../../../utils/helper";

function ParticipantListItem({
  participantId,
  raisedHand,
  isBot,
  customName,
  activePanelSpeaker,
  showCountdown,
  isBotSpeaking,
  isPNLO,
}) {
  const { micOn, webcamOn, displayName, isLocal } =
    useParticipant(participantId) || {};

  const panelDetails = JSON.parse(
    sessionStorage.getItem("panelDetails") || "{}"
  );

  const normalized = (val = "") => val.trim().toLowerCase();

  // Determine bot name dynamically (no hardcoding)
  const botName =
    panelDetails?.botName ||
    panelDetails?.pnlf1 ||
    panelDetails?.pnlf2 ||
    panelDetails?.pnlm1 ||
    panelDetails?.pnlm2 ||
    "Assistant";

  // Determine name
  let finalDisplayName = isLocal ? "You" : customName || displayName;
  if (!finalDisplayName && isBot) finalDisplayName = botName;

  // Determine active speaker
  let isActiveSpeaker =
    activePanelSpeaker &&
    normalized(activePanelSpeaker) === normalized(finalDisplayName);

  if (isBot && isBotSpeaking && !showCountdown && isPNLO) {
    isActiveSpeaker = true;
  }

  return (
    <div className="mt-2 m-2 p-2 bg-gray-700 rounded-lg mb-0">
      <div className="flex flex-1 items-center justify-center relative">
        {/* Avatar */}
        <div
          style={{
            color: "#212032",
            backgroundColor: "#757575",
          }}
          className="h-10 w-10 text-lg mt-0 rounded overflow-hidden flex relative items-center justify-center"
        >
          {finalDisplayName?.charAt(0)?.toUpperCase()}
        </div>

        {/* Dynamic Name Display */}
        <div className="ml-2 mr-1 flex flex-1">
          <p className="text-base text-white overflow-hidden whitespace-pre-wrap font-capitalize overflow-ellipsis">
            {(() => {
              const display = finalDisplayName || botName || "Participant";
              return nameTructed(display, 15);
            })()}
          </p>
        </div>

        {/* Raised Hand */}
        {raisedHand && (
          <div className="flex items-center justify-center m-1 p-1">
            <RaiseHand fillcolor={"#fff"} />
          </div>
        )}

        {/* Mic + speaking visual */}
        <div className="m-1 p-1 relative flex items-center gap-1">
          {(isActiveSpeaker && !showCountdown && isBotSpeaking) || micOn ? (
            <MicOnIcon />
          ) : (
            <MicOffIcon />
          )}
          {isActiveSpeaker && !showCountdown && isBotSpeaking && (
            <div className="flex space-x-0.5 ml-1">
              <div
                className="w-0.5 h-2 bg-[#08F423] animate-pulse rounded"
                style={{ animationDuration: "1s" }}
              ></div>
              <div
                className="w-0.5 h-3 bg-[#08F423] animate-pulse rounded"
                style={{ animationDelay: "0.2s", animationDuration: "1s" }}
              ></div>
              <div
                className="w-0.5 h-2 bg-[#08F423] animate-pulse rounded"
                style={{ animationDelay: "0.4s", animationDuration: "1s" }}
              ></div>
            </div>
          )}
        </div>

        {/* Video icons */}
        <div className="m-1 p-1">
          {finalDisplayName === "You" ? (
            webcamOn ? (
              <VideoCamOnIcon />
            ) : (
              <VideoCamOffIcon />
            )
          ) : (
            <VideoCamOffIcon />
          )}
        </div>
      </div>
    </div>
  );
}

export function ParticipantPanel({
  panelHeight,
  activePanelSpeaker,
  showCountdown,
  isBotSpeaking,
}) {
  const { raisedHandsParticipants, aiAssistants = [] } = useMeetingAppContext();
  const mMeeting = useMeeting();
  const participants = mMeeting.participants;

  const panelDetails = JSON.parse(
    sessionStorage.getItem("panelDetails") || "{}"
  );
  const { panel, interview_id, ...others } = panelDetails;

  const isPNLO = panel === "PNLO";

  const customNames = Object.values(others).filter(
    (v) => typeof v === "string" && v.trim() !== ""
  );

  const participantIds = [...participants.keys()];
  const allParticipantIds = [
    ...participantIds,
    ...aiAssistants
      .filter((assistant) => !participantIds.includes(assistant.participantId))
      .map((assistant) => assistant.participantId),
  ];

  const raisedSorted = raisedHandsParticipants.sort(
    (a, b) => b.raisedHandOn - a.raisedHandOn
  );
  const notRaised = allParticipantIds.filter(
    (pID) =>
      !raisedHandsParticipants.find(
        ({ participantId }) => participantId === pID
      )
  );

  const combined = [
    ...raisedSorted.map(({ participantId }) => ({
      raisedHand: true,
      participantId,
      isBot: aiAssistants.some((bot) => bot.participantId === participantId),
    })),
    ...notRaised.map((p) => ({
      raisedHand: false,
      participantId: p,
      isBot: aiAssistants.some((bot) => bot.participantId === p),
    })),
  ];

  let finalList = [];

  if (!isPNLO) {
    const youUser = {
      participantId: "you_user",
      raisedHand: false,
      isBot: false,
      customName: "You",
    };

    const dynamicUsers = customNames.map((name, i) => ({
      participantId: `custom_${i}`,
      raisedHand: false,
      isBot: false,
      customName: name,
    }));

    finalList = [youUser, ...dynamicUsers];
  } else {
    finalList = combined;
  }

  return (
    <div
      className="flex w-full flex-col bg-gray-750 overflow-y-auto"
      style={{ height: panelHeight }}
    >
      <div
        className="flex flex-col flex-1"
        style={{ height: panelHeight - 100 }}
      >
        {finalList.map((participant) => (
          <ParticipantListItem
            key={participant.participantId}
            participantId={participant.participantId}
            raisedHand={participant.raisedHand}
            isBot={participant.isBot}
            customName={participant.customName}
            activePanelSpeaker={activePanelSpeaker}
            showCountdown={showCountdown}
            isBotSpeaking={isBotSpeaking}
            isPNLO={isPNLO}
          />
        ))}
      </div>
    </div>
  );
}
